import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  Clock, 
  Download, 
  CheckCircle, 
  ArrowRight, 
  Target,
  Users,
  TrendingUp,
  FileText,
  Award,
  MessageSquare,
  BookOpen,
  Lightbulb,
  Trophy,
  Lock,
  DollarSign,
  BarChart3,
  Play,
  Home,
  GraduationCap
} from 'lucide-react';
// Navbar is now handled by layout components
import { Footer } from './Footer';
import { apiUrl } from '../config/api';

interface User {
  id: string;
  email: string;
  fullName: string;
  company: string;
  role: string;
  userType: string;
  status: string;
}

interface Module1ContentProps {
  user: User | null;
  onLogout: () => void;
  isPreviewMode?: boolean;
  invitationEmail?: string;
  isAdminMode?: boolean;
  inviteToken?: string;
}

// Helper function to extract first name from full name
function getFirstName(fullName: string | null | undefined): string {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  return parts[0] || '';
}

export function Module1Content({ user, onLogout, isPreviewMode = false, invitationEmail: propInvitationEmail, isAdminMode = false, inviteToken }: Module1ContentProps) {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [userChallenges, setUserChallenges] = useState(['', '', '']);
  const [quizAnswers, setQuizAnswers] = useState<{ [key: number]: string }>({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const [isModuleCompleted, setIsModuleCompleted] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Function to handle section changes with smooth scrolling
  const handleSectionChange = (newSection: number) => {
    setCurrentSection(newSection);
    // Scroll to top of content area smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };


  // Remove old localStorage-based completion check - we now use database-backed progress tracking
  // The completion status is fetched from the API and shown in the UI appropriately


  // Track course start when user first accesses Module 1
  useEffect(() => {
    const trackCourseStart = async () => {
      // Only track for authenticated users, not preview mode or admin mode
      if (!user || isPreviewMode || isAdminMode) {
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          return;
        }

        const response = await fetch('/api/course-start', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ moduleId: 1 })
        });

        if (response.ok) {
          console.log('✅ Course start tracked');
        }
      } catch (error) {
        console.error('Error tracking course start:', error);
        // Don't show error to user, just log it
      }
    };

    trackCourseStart();
  }, [user, isPreviewMode, isAdminMode]);

  // Check if Module 1 is already completed when component loads
  useEffect(() => {
    const checkModuleCompletion = async () => {
      // Skip check for preview mode, but allow admin mode to check (for display purposes)
      if (!user || isPreviewMode) {
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          return;
        }

        const response = await fetch(apiUrl('/api/user-progress'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.success && data.data.completedModuleIds) {
          const isCompleted = data.data.completedModuleIds.includes(1);
          if (isCompleted) {
            setIsModuleCompleted(true);
            console.log('✅ Module 1 is already completed');
          }
        }
      } catch (error) {
        console.error('Error checking module completion:', error);
        // Don't show error to user, just log it
      }
    };

    checkModuleCompletion();
  }, [user, isPreviewMode]);

  // Debug: Log when completion modal state changes
  useEffect(() => {
    if (showCompletionModal) {
      console.log('🎯 Completion modal is now visible. isAdminMode:', isAdminMode, 'isModuleCompleted:', isModuleCompleted);
    }
  }, [showCompletionModal, isAdminMode, isModuleCompleted]);

  // Disable auto-show of Request Full Access form; it should only open from the Congratulations modal button
  useEffect(() => {
    // intentionally no-op
  }, [currentSection, isPreviewMode, isModuleCompleted]);

  const handleResourceDownload = (filename: string) => {
    try {
      // Use direct public URL for downloads
      const link = document.createElement('a');
      link.href = `/downloads/${filename}`;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  const sections = [
    { id: 'welcome', title: 'Welcome', icon: BookOpen },
    { id: 'why-matters', title: 'Why Principal Talent Matters', icon: Target },
    { id: 'challenges', title: 'Czech Market Challenges', icon: TrendingUp },
    { id: 'activity', title: 'Reflect on Your Challenges', icon: MessageSquare },
    { id: 'quiz', title: 'Test Your Knowledge', icon: Trophy },
    { id: 'takeaway', title: 'Takeaway Resources', icon: Download }
  ];

  const quizQuestions = [
    {
      question: "What percentage of Czech IT companies struggle to hire senior talent in 2025?",
      options: ["25%", "63%", "80%", "10%"],
      correct: "63%",
      explanation: "63% of Czech IT firms face senior talent shortages due to skills gaps and competition."
    },
    {
      question: "What's the average salary range for senior IT professionals in the Czech Republic?",
      options: ["€40,000-60,000", "€80,000-120,000", "€150,000-200,000", "€20,000-40,000"],
      correct: "€80,000-120,000",
      explanation: "Senior IT professionals in CZ earn €80,000-120,000, significantly less than remote US roles."
    },
    {
      question: "Why do many Czech TA teams struggle to hire seniors?",
      options: ["Lack of applicants", "Overtrained staff", "Undertrained staff, often from HR roles", "Too many remote jobs in CZ"],
      correct: "Undertrained staff, often from HR roles",
      explanation: "Many TA teams lack specialized recruitment training, coming from general HR backgrounds."
    },
    {
      question: "What's a key post-COVID trend affecting Czech IT hiring?",
      options: ["Seniors prefer local jobs", "Seniors seek better-paid remote roles abroad", "Unemployment is high", "Companies overpay locally"],
      correct: "Seniors seek better-paid remote roles abroad",
      explanation: "Post-COVID, senior talent increasingly seeks remote roles with higher international salaries."
    },
    {
      question: "What's a common mistake in sourcing principal IT talent?",
      options: ["Using niche tech forums", "Relying only on LinkedIn", "Offering referral bonuses", "Engaging at meetups"],
      correct: "Relying only on LinkedIn",
      explanation: "Principal talent requires creative sourcing beyond traditional platforms like LinkedIn."
    }
  ];

  const handleChallengeChange = (index: number, value: string) => {
    const newChallenges = [...userChallenges];
    newChallenges[index] = value;
    setUserChallenges(newChallenges);
  };

  const handleQuizAnswer = (questionIndex: number, answer: string) => {
    setQuizAnswers({ ...quizAnswers, [questionIndex]: answer });
  };

  const submitQuiz = () => {
    setShowQuizResults(true);
    setProgress(100);
  };

  const handleSubmitChallenges = async () => {
    // Validate that all challenges are filled
    const filledChallenges = userChallenges.filter(challenge => challenge.trim() !== '');
    if (filledChallenges.length === 0) {
      setSubmitMessage('Please fill in at least one challenge before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const submissionData = {
        moduleId: '1',
        challenges: filledChallenges,
        quizAnswers: Object.keys(quizAnswers).length > 0 ? quizAnswers : undefined,
        quizScore: Object.keys(quizAnswers).length > 0 ? getScore() : undefined,
        userEmail: user?.email || propInvitationEmail || undefined,
        userToken: inviteToken || undefined,
        userName: user?.fullName || undefined,
        company: user?.company || undefined
      };

      const response = await fetch(apiUrl('/api/module-submissions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (result.success) {
        setSubmitMessage('✅ Your challenges have been submitted successfully! We\'ll review them and get back to you soon.');
        // Clear the form
        setUserChallenges(['', '', '']);
      } else {
        setSubmitMessage(`❌ Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error submitting challenges:', error);
      setSubmitMessage('❌ Error submitting your challenges. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteModule = async () => {
    // Prevent multiple clicks if already completing
    if (isCompleting) {
      console.log('⚠️ Already completing, ignoring click');
      return;
    }
    
    // If already completed, just show the modal again (don't try to complete again)
    if (isModuleCompleted) {
      console.log('⚠️ Module already completed, showing completion modal');
      setShowCompletionModal(true);
      return;
    }

    setIsCompleting(true);

    try {
      const completionData = {
        moduleId: '1',
        completedAt: new Date().toISOString(),
        userEmail: user?.email || propInvitationEmail || undefined,
        userToken: inviteToken || undefined,
        userName: user?.fullName || undefined,
        company: user?.company || undefined,
        isAdminMode: isAdminMode, // Flag to mark admin testing completions
        completionDetails: {
          challengesSubmitted: userChallenges.filter(c => c.trim() !== '').length > 0,
          quizCompleted: Object.keys(quizAnswers).length > 0,
          quizScore: Object.keys(quizAnswers).length > 0 ? getScore() : undefined,
          sectionsViewed: currentSection + 1
        }
      };

      const response = await fetch(apiUrl('/api/module-submissions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
        },
        body: JSON.stringify(completionData)
      });

      const result = await response.json();

      if (result.success) {
        // Check if module was already completed (only for non-admin completions)
        if (result.data?.alreadyCompleted && !isAdminMode) {
          console.log('⚠️ Module already completed, preventing duplicate completion');
          setIsModuleCompleted(true);
          setIsCompleting(false);
          // Still show modal for user to see completion message
          setShowCompletionModal(true);
          return;
        }

        // Module completion saved to database with userId
        // Set state immediately
        setIsModuleCompleted(true);
        
        // ALWAYS show completion modal (for both admin and regular users)
        setShowCompletionModal(true);
        console.log('✅ Completion modal should be visible now');
        
        // Refresh progress from API to ensure consistency (skip for admin mode)
        if (user && !isPreviewMode && !isAdminMode) {
          try {
            const token = localStorage.getItem('authToken');
            if (token) {
              const progressResponse = await fetch(apiUrl('/api/user-progress'), {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                }
              });
              const progressData = await progressResponse.json();
              if (progressData.success && progressData.data.completedModuleIds) {
                const isCompleted = progressData.data.completedModuleIds.includes(1);
                setIsModuleCompleted(isCompleted);
                console.log('✅ Progress refreshed, Module 1 completed:', isCompleted);
              }
            }
          } catch (progressError) {
            console.error('Error refreshing progress:', progressError);
            // Don't fail the completion if progress refresh fails
          }
        }
        
        // If admin mode, show special message
        if (isAdminMode) {
          console.log('✅ Admin test completion recorded (does not affect user progress)');
        } else {
          console.log('✅ Module completion saved for user');
        }
        
        // Reset completing state after successful completion
        setIsCompleting(false);
      } else {
        console.error('❌ Error completing module:', result.message);
        alert('Failed to complete module. Please try again.');
        // If there's an error, don't set as completed
        setIsCompleting(false);
      }
    } catch (error) {
      console.error('Error completing module:', error);
      // If there's an error, don't set as completed
      setIsCompleting(false);
    }
  };

  const getScore = () => {
    let correct = 0;
    quizQuestions.forEach((q, index) => {
      if (quizAnswers[index] === q.correct) correct++;
    });
    return Math.round((correct / quizQuestions.length) * 100);
  };

  const renderSection = () => {
    switch (currentSection) {
      case 0: // Welcome
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <BookOpen className="section-icon book-icon" />
              <h2>Welcome to Sourcing Principal IT Talent</h2>
            </div>

            <div className="module-meta module-info-grid" style={{ marginBottom: '2rem' }}>
              <div className="meta-item module-info-item">
                <Clock className="info-icon" style={{ width: '32px', height: '32px' }} />
                <span className="info-text">20 minutes (self-paced)</span>
              </div>
              <div className="meta-item module-info-item">
                <Target className="info-icon" style={{ width: '32px', height: '32px' }} />
                <span className="info-text">TA teams at US and Czech companies</span>
              </div>
              <div className="meta-item module-info-item">
                <Award className="info-icon" style={{ width: '32px', height: '32px' }} />
                <span className="info-text">Practical skills & knowledge</span>
              </div>
            </div>
            
            <div className="welcome-content">
              <div className="instructor-intro user-section">
                <Users className="avatar-icon user-icon" />
                <div className="instructor-info user-content">
                  <h3 className="user-name">Hi, {user?.fullName ? getFirstName(user.fullName) : 'welcome'}</h3>
                  <p className="instructor-bio user-description">
                    Spending over 20 years helping tech, fintech and other companies find the best senior IT talent worldwide, it's important to understand why Principal IT Talent matters. 
                    In this module, you'll learn why these professionals are so critical and how to tackle the unique challenges of hiring them. 
                    My goal is to share practical insights from my career so you can find top talent—because there's always a way if you know where to look and understand the market.
                  </p>
                </div>
              </div>
              
              <div className="module-objectives">
                <h4>What You'll Learn:</h4>
                <ul>
                  <li><CheckCircle className="check-icon" /> Why principal IT talent is crucial for innovation</li>
                  <li><CheckCircle className="check-icon" /> Czech and US market challenges and opportunities</li>
                  <li><CheckCircle className="check-icon" /> Creative sourcing strategies beyond LinkedIn</li>
                  <li><CheckCircle className="check-icon" /> Real-world examples from top tech companies</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 1: // Why Principal Talent Matters
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <Target className="section-icon" />
              <h2>Why Principal IT Talent Matters</h2>
            </div>
            
            <div className="content-body">
              <p className="lead-text">
                Principal IT professionals - those with 10+ years of experience - are the backbone of innovation in tech companies. 
                They lead critical projects, like building AI platforms or securing cybersecurity frameworks, and mentor teams to drive success.
              </p>
              
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">63%</div>
                  <div className="stat-label">Companies struggling with senior hiring</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">€80k-120k</div>
                  <div className="stat-label">Average senior salary in CZ</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">15,000+</div>
                  <div className="stat-label">New IT jobs projected 2025</div>
                </div>
              </div>

              <div className="story-highlight">
                <div className="story-content">
                  <Lightbulb className="story-icon" />
                  <h4>Real Story: Cybersecurity Specialists</h4>
                  <p>
                    A leading cybersecurity firm needed Malware Analysts — highly mathematical experts who could dive into low-level programming 
                    at the kernel level. These candidates were incredibly rare, and neither LinkedIn nor job boards were enough. 
                    By tapping into niche tech communities and leveraging my network, I found five exceptional analysts who helped 
                    strengthen the company's global reputation.
                  </p>
                  <div className="story-takeaway">
                    <strong>The lesson:</strong> When you know your stuff and look beyond the usual channels, you can find the talent others miss.
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Challenges
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <TrendingUp className="section-icon" />
              <h2>Challenges in the IT Market</h2>
            </div>
            
            <div className="content-body">
              <p className="lead-text">
                Hiring senior IT talent in the US and Czech Republic is tough. With unemployment at just 2-3%, top professionals have their pick of jobs.
              </p>

              <div className="challenges-grid">
                <div className="challenge-card">
                  <div className="challenge-card-header">
                    <DollarSign className="challenge-icon" />
                    <h4>Salary Competition</h4>
                  </div>
                  <p>Senior developers can earn $150,000-250,000 in the US versus €80,000-120,000 in Czech Republic.</p>
                </div>
                <div className="challenge-card">
                  <div className="challenge-card-header">
                    <BarChart3 className="challenge-icon" />
                    <h4>Application Overload</h4>
                  </div>
                  <p>Sometimes 100+ applicants per role, but most lack specialised skills needed for principal positions.</p>
                </div>
                <div className="challenge-card">
                  <div className="challenge-card-header">
                    <GraduationCap className="challenge-icon" />
                    <h4>Training Gaps</h4>
                  </div>
                  <p>Many TA teams lack deep recruitment experience, coming from HR or office management roles.</p>
                </div>
                <div className="challenge-card">
                  <div className="challenge-card-header">
                    <TrendingUp className="challenge-icon" />
                    <h4>Market Predictions</h4>
                  </div>
                  <p>Czech Republic: 3.5 job seekers per vacancy. US: 8.8M openings with 4.3-4.5% unemployment rates.</p>
                </div>
              </div>

              {/* Unemployment Reports */}
              <div className="unemployment-reports" style={{ margin: '3rem 0' }}>
                <div className="report-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  marginBottom: '2rem',
                  borderLeft: '4px solid var(--orange)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <h4 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '1rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Short Report: Unemployment in the Czech Republic (Focus on IT Sector, 2025)
                  </h4>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h5 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                      Monthly Overall Unemployment (2025 Avg: ~4.3%):
                    </h5>
                    <p style={{ color: 'var(--gray)', lineHeight: '1.6', marginBottom: '1rem' }}>
                      Started at 4.3% (Jan), dipped to 4.2% (May-Jun), rose to 4.5% (Aug-Sep). Industrial slowdowns drove increases, but services remained stable.
                    </p>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <h5 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                      IT Sector Monthly Highlights (Avg: ~0.6%):
                    </h5>
                    <p style={{ color: 'var(--gray)', lineHeight: '1.6', marginBottom: '1rem' }}>
                      Rates stayed low (0.5-0.7%), with minimal job seekers (~4,000/month) and high vacancies (2.5+ per worker). Demand in AI/cybersecurity offset any pressures; Prague hubs at ~0.4%.
                    </p>
                  </div>

                  <div>
                    <h5 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                      Quarterly Comparison (Overall Rates):
                    </h5>
                    <div className="quarterly-table" style={{ 
                      background: 'rgba(255, 255, 255, 0.5)', 
                      borderRadius: '8px', 
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <table style={{ width: '100%', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600' }}>Quarter</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600' }}>2023</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600' }}>2024</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600' }}>2025</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600' }}>Change (2025 vs. 2024)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ padding: '0.5rem', fontWeight: '500' }}>Q1</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.7%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.9%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.3%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>+0.4 pp</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '0.5rem', fontWeight: '500' }}>Q2</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.8%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.0%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.2%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>+0.2 pp</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '0.5rem', fontWeight: '500' }}>Q3</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.9%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.1%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.5%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>+0.4 pp</td>
                          </tr>
                          <tr style={{ borderTop: '1px solid #e5e7eb', fontWeight: '600' }}>
                            <td style={{ padding: '0.5rem' }}>Annual</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.8%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.0%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>~4.3%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>+0.3 pp</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p style={{ color: 'var(--gray)', lineHeight: '1.6', fontSize: '0.9rem', fontStyle: 'italic' }}>
                      IT sector stable at ~0.6% across years, supporting tight market for talent.
                    </p>
                  </div>
                </div>

                <div className="report-card" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  borderLeft: '4px solid var(--orange)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <h4 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    marginBottom: '1rem',
                    fontFamily: 'var(--montserrat)'
                  }}>
                    Short Report: Unemployment in the United States (Focus on IT Sector, 2025)
                  </h4>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h5 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                      Monthly Overall Unemployment (2025 Avg: ~4.2%):
                    </h5>
                    <p style={{ color: 'var(--gray)', lineHeight: '1.6', marginBottom: '1rem' }}>
                      Rose from 4.0% (Jan) to 4.3% (Aug), with a dip to 4.1% (Jun). Slower job growth (~100k/month) amid economic cooling.
                    </p>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <h5 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                      IT Sector Monthly Highlights (Avg: ~4.7%):
                    </h5>
                    <p style={{ color: 'var(--gray)', lineHeight: '1.6', marginBottom: '1rem' }}>
                      Rates varied (4.0-5.5%), higher than national due to tech adjustments, but resilient with ~2% employment growth. High demand for AI roles; ~317k annual openings despite skills gaps.
                    </p>
                  </div>

                  <div>
                    <h5 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--dark)', marginBottom: '0.5rem' }}>
                      Quarterly Comparison (Overall Rates):
                    </h5>
                    <div className="quarterly-table" style={{ 
                      background: 'rgba(255, 255, 255, 0.5)', 
                      borderRadius: '8px', 
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <table style={{ width: '100%', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600' }}>Quarter</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600' }}>2023</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600' }}>2024</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600' }}>2025</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600' }}>Change (2025 vs. 2024)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ padding: '0.5rem', fontWeight: '500' }}>Q1</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.5%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.8%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.1%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>+0.3 pp</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '0.5rem', fontWeight: '500' }}>Q2</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.5%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.0%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.2%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>+0.2 pp</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '0.5rem', fontWeight: '500' }}>Q3</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.7%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.2%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.3%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>+0.1 pp</td>
                          </tr>
                          <tr style={{ borderTop: '1px solid #e5e7eb', fontWeight: '600' }}>
                            <td style={{ padding: '0.5rem' }}>Annual</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>3.6%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>4.0%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>~4.2%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>+0.2 pp</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p style={{ color: 'var(--gray)', lineHeight: '1.6', fontSize: '0.9rem', fontStyle: 'italic' }}>
                      IT sector ~4.7% in 2025, up slightly from 2023-2024 (~4.5-4.6%), reflecting competition for skilled hires.
                    </p>
                  </div>
                </div>
              </div>

              <div className="story-highlight">
                <div className="story-content">
                  <Lightbulb className="story-icon" />
                  <h4>Real Story: High-Tech C++ Developer</h4>
                  <p>
                    A high-tech company needed a C++ developer who could pass their rigorous interview process. 
                    Most recruiters struck out, but by digging into niche developer forums and understanding the company's technical needs, 
                    I found a candidate who not only got the offer but became a key contributor.
                  </p>
                  <div className="story-takeaway">
                    <strong>The lesson:</strong> With the right approach, even the toughest roles can be filled.
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Activity
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <MessageSquare className="section-icon" />
              <h2>Reflect on Your Hiring Challenges</h2>
            </div>
            
            <div className="content-body">
              <p className="lead-text">
                Take a moment to think about your biggest hurdles in hiring senior IT talent. What's stopping you from finding 
                the right principal engineers or specialists?
              </p>

              <div className="activity-container">
                <h4>Write down your top 3 challenges:</h4>
                {userChallenges.map((challenge, index) => (
                  <div key={index} className="challenge-input-group">
                    <label>Challenge {index + 1}:</label>
                    <input
                      type="text"
                      value={challenge}
                      onChange={(e) => handleChallengeChange(index, e.target.value)}
                      placeholder={`e.g., ${[
                        "Too many unqualified applicants flooding our pipeline",
                        "Senior candidates ignore our job postings", 
                        "Competition from big tech companies like JetBrains"
                      ][index]}`}
                      className="challenge-input"
                    />
                  </div>
                ))}
                
                <button 
                  className="challenge-submit-btn"
                  onClick={handleSubmitChallenges}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit My Challenges'}
                </button>
                
                {submitMessage && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    borderRadius: '8px',
                    background: submitMessage.includes('✅') ? '#f0f9ff' : '#fef2f2',
                    border: `1px solid ${submitMessage.includes('✅') ? '#bae6fd' : '#fecaca'}`,
                    color: submitMessage.includes('✅') ? '#1e40af' : '#991b1b',
                    fontSize: '0.875rem'
                  }}>
                    {submitMessage}
                  </div>
                )}
                
                <div className="activity-note">
                  <Lock className="note-icon" />
                  <span>Your input will be sent to us for optional personalised feedback</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 4: // Quiz
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <Trophy className="section-icon" />
              <h2>Test Your Market Knowledge</h2>
            </div>
            
            <div className="content-body">
              {!showQuizResults ? (
                <div className="quiz-container">
                  {quizQuestions.map((question, index) => (
                    <div key={index} className="quiz-question">
                      <h4>Question {index + 1}: {question.question}</h4>
                      <div className="quiz-options">
                        {question.options.map((option, optionIndex) => (
                          <label key={optionIndex} className="quiz-option">
                            <input
                              type="radio"
                              name={`question-${index}`}
                              value={option}
                              checked={quizAnswers[index] === option}
                              onChange={() => handleQuizAnswer(index, option)}
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  <button 
                    onClick={submitQuiz}
                    className="submit-quiz-btn"
                    disabled={Object.keys(quizAnswers).length < quizQuestions.length}
                  >
                    Submit Quiz
                  </button>
                </div>
              ) : (
                <div className="quiz-results">
                  <div className="score-display">
                    <h3>Quiz Results</h3>
                    <div className="score">{getScore()}%</div>
                    <p>You got {Object.values(quizAnswers).filter((answer, index) => 
                      answer === quizQuestions[index].correct
                    ).length} out of {quizQuestions.length} questions correct!</p>
                  </div>
                  
                  <div className="answer-review">
                    {quizQuestions.map((question, index) => (
                      <div key={index} className="answer-item">
                        <h5>Question {index + 1}</h5>
                        <p>{question.question}</p>
                        <div className={`answer-result ${quizAnswers[index] === question.correct ? 'correct' : 'incorrect'}`}>
                          <div className="user-answer">
                            <span className="answer-label">Your answer:</span>
                            <span className="answer-value">{quizAnswers[index]}</span>
                          </div>
                          <div className="correct-answer">
                            <span className="answer-label">Correct answer:</span>
                            <span className="answer-value">{question.correct}</span>
                          </div>
                          <div className="explanation">{question.explanation}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 5: // Takeaway
        return (
          <div className="module-section">
            <div className="section-header title-with-icon">
              <Download className="section-icon" />
              <h2>Takeaway Resources</h2>
            </div>
            
            <div className="content-body">
              <div className="resource-card">
                <div className="resource-header">
                  <div className="resource-icon">
                    <FileText />
                  </div>
                  <div className="resource-info">
                    <h3>Czech IT Talent Market 2025 Checklist</h3>
                    <p>Downloadable PDF with key stats, sourcing tips, and actionable strategies</p>
                  </div>
                </div>
                <button 
                  className="download-btn"
                  onClick={() => handleResourceDownload('czech-it-talent-market-2025-checklist.pdf')}
                >
                  <Download />
                  Download PDF
                </button>
              </div>

              <div className="resource-card">
                <div className="resource-header">
                  <div className="resource-icon">
                    <FileText />
                  </div>
                  <div className="resource-info">
                    <h3>US IT Talent Market 2025 Checklist</h3>
                    <p>Downloadable PDF with US market insights, salary benchmarks, and sourcing strategies</p>
                  </div>
                </div>
                <button 
                  className="download-btn"
                  onClick={() => handleResourceDownload('us-it-talent-market-2025-checklist.pdf')}
                >
                  <Download />
                  Download PDF
                </button>
              </div>

              <div className="checklist-preview">
                <h4>What's Inside Both Checklists:</h4>
                <ul>
                  <li><CheckCircle />Key market statistics and trends</li>
                  <li><CheckCircle />Principal talent identification checklist</li>
                  <li><CheckCircle />Creative sourcing strategies</li>
                  <li><CheckCircle />Salary benchmarks and negotiation tips</li>
                  <li><CheckCircle />TA team training recommendations</li>
                  <li><CheckCircle />Regional market comparisons (Czech vs US)</li>
                  <li><CheckCircle />Cultural considerations for international hiring</li>
                </ul>
              </div>

            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ 
      minHeight: 'calc(100vh - 6rem)', 
      background: 'var(--white)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      paddingTop: '2rem'
    }}>
      {/* Navbar is now handled by layout components */}


      <main className="module-content" style={{ 
        paddingBottom: '2rem',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="container">
          {/* Fixed Header Section */}
          <div className="module-fixed-header">
          {!isPreviewMode && (
            <Link to={user ? `/user/${user.id}/dashboard` : "/"} className="back-link">
              <ChevronLeft />
              Back to Dashboard
            </Link>
          )}

          <div className="module-header">
            <div className="module-badge">
              Module 1
            </div>
            <h1>Introduction to Principal IT Talent Challenges</h1>
            <div className="module-meta">
              <div className="meta-item">
                <Clock />
                <span>20 minutes</span>
              </div>
              <div className="meta-item">
                <BookOpen />
                <span>materials</span>
              </div>
            </div>
            <p className="module-description">
              Understanding the Czech IT market landscape and why securing principal-level talent has become 
              the defining challenge for technology companies in 2025.
            </p>
            </div>
          </div>

          {/* Progress Timeline for Tablet/Mobile */}
          <div className="progress-timeline">
            <div className="timeline-container">
              {sections.map((section, index) => {
                const Icon = section.icon;
                const isActive = currentSection === index;
                const isCompleted = index < currentSection;
                const isUpcoming = index > currentSection;
                
                return (
                  <div key={section.id} className="timeline-step">
                    <button
                      className={`timeline-button ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isUpcoming ? 'upcoming' : ''}`}
                      onClick={() => handleSectionChange(index)}
                    >
                      <div className="timeline-icon">
                        {isCompleted ? <CheckCircle size={18} color="#10b981" /> : <Icon size={18} />}
                      </div>
                      <div className="timeline-label">{section.title}</div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Scrollable Content Layout */}
          <div className="module-layout">
            <div className="module-sidebar">
              <div className="progress-section">
                <h3>Module Progress</h3>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
                  />
                </div>
                <span>{Math.round(((currentSection + 1) / sections.length) * 100)}% Complete</span>
              </div>

              <nav className="section-nav">
                <h3>Sections</h3>
                {sections.map((section, index) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      className={`nav-item ${currentSection === index ? 'active' : ''} ${index < currentSection ? 'completed' : ''}`}
                      onClick={() => handleSectionChange(index)}
                    >
                      <Icon className="nav-icon" />
                      <span>{section.title}</span>
                      {index < currentSection && <CheckCircle className="completed-icon" />}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="module-content-wrapper">
              <div className="module-content">
                {renderSection()}
              </div>
              
              <div className={`section-navigation ${currentSection === 0 ? 'single-button' : ''}`}>
              {currentSection > 0 && (
                <button 
                  className="nav-btn prev-btn"
                  onClick={() => handleSectionChange(currentSection - 1)}
                >
                  <ChevronLeft />
                  Previous
                </button>
              )}
              
              {currentSection < sections.length - 1 && (
                <button 
                  className="nav-btn next-btn"
                  onClick={() => handleSectionChange(currentSection + 1)}
                >
                  Next
                  <ArrowRight />
                </button>
              )}
              
              {currentSection === sections.length - 1 && (
                <div>
                  {/* Always show complete button - modal will handle the completion message */}
                  <button 
                    className="nav-btn complete-btn"
                    onClick={handleCompleteModule}
                    disabled={isCompleting}
                    style={{
                      background: isModuleCompleted ? '#10b981' : undefined,
                      color: isModuleCompleted ? 'white' : undefined,
                      cursor: isCompleting ? 'not-allowed' : 'pointer',
                      opacity: isCompleting ? 0.6 : 1,
                      pointerEvents: isCompleting ? 'none' : 'auto'
                    }}
                  >
                    {isCompleting ? 'Completing...' : isModuleCompleted ? 'MODULE 1 COMPLETED' : 'Complete Module'}
                    <Award />
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </main>

      
      {/* Module Completion Modal */}
      {showCompletionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem auto'
            }}>
              <CheckCircle size={40} color="white" />
            </div>
            
            <h2 style={{
              color: 'var(--dark)',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '1rem',
              fontFamily: 'Montserrat, Arial, sans-serif'
            }}>
              🎉 Module 1 - Introduction to Principal IT Talent Challenges Completed!
            </h2>
            
            <p style={{
              color: 'var(--gray)',
              fontSize: '1rem',
              lineHeight: '1.6',
              marginBottom: '2rem',
              fontFamily: 'Montserrat, Arial, sans-serif'
            }}>
              🎉 Module 1 - Introduction to Principal IT Talent Challenges completed! You're ready to continue with the next module.
            </p>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              alignItems: 'stretch',
              width: '100%'
            }}>
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  if (user) {
                    // Navigate to Module 2 - works for both admin and regular users
                    // AdminLayout will be applied automatically via UserModule2Route
                    navigate(`/user/${user.id}/module2`);
                  } else {
                    navigate('/');
                  }
                }}
                style={{
                  background: 'var(--dark)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  width: '100%'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#042789';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Play size={16} />
                Continue to Module 2
              </button>
              
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  // Navigate to admin dashboard if in admin mode, otherwise user dashboard
                  if (isAdminMode) {
                    window.location.href = '/admin/dashboard';
                  } else {
                    window.location.href = `/user/${user?.id}/dashboard`;
                  }
                }}
                style={{
                  background: 'var(--orange)',
                  color: 'var(--dark)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Montserrat, Arial, sans-serif',
                  width: '100%'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#c4a020';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--orange)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Home size={16} />
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
      </div>
    );
  }

export default Module1Content;
